import { ArrowLeft, Clock3, CreditCard, LifeBuoy, Upload } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { fetchVipPaymentQr, getVipOrder, requestVipOrderRefund, submitVipOrderProof } from '../api.js'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'
import { vipPaymentQrEndpoint } from '../vip-payment-config.js'

const STATUS_LABELS = {
  pending: '待付款',
  submitted: '已提交付款证明',
  paid: '已确认开通',
  rejected: '已拒绝',
  refund_requested: '已申请退款',
  refunded: '已退款',
  expired: '已过期',
}

function VipOrderPage() {
  const { orderNo } = useParams()
  const [order, setOrder] = useState(null)
  const [paymentConfig, setPaymentConfig] = useState({})
  const [form, setForm] = useState({
    payChannel: 'wechat',
    payNickname: '',
    payTime: '',
    proofImageUrl: '',
    contact: '',
    reason: '',
  })
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [qrObjectUrl, setQrObjectUrl] = useState('')
  const [qrLoading, setQrLoading] = useState(false)
  const [qrLoadError, setQrLoadError] = useState('')

  useEffect(() => setPageMetadata({
    title: 'VIP 订单 - 青途 AI',
    description: '查看 VIP 订单并提交付款证明。',
    robots: NOINDEX_ROBOTS,
  }), [])

  const loadOrder = useCallback(async (signal) => {
    setLoading(true)
    setError('')
    try {
      const result = await getVipOrder(orderNo, signal)
      setOrder(result?.order || null)
      setPaymentConfig(result?.paymentConfig || {})
    } catch (loadError) {
      if (loadError.name !== 'AbortError') setError(loadError.message || '订单加载失败')
    } finally {
      if (!signal?.aborted) setLoading(false)
    }
  }, [orderNo])

  useEffect(() => {
    const controller = new AbortController()
    loadOrder(controller.signal)
    return () => controller.abort()
  }, [loadOrder])

  async function submitProof(event) {
    event.preventDefault()
    setBusy('proof')
    setError('')
    setNotice('')
    try {
      const result = await submitVipOrderProof(orderNo, {
        payChannel: form.payChannel,
        payNickname: form.payNickname,
        payTime: form.payTime,
        proofImageUrl: form.proofImageUrl,
      })
      setOrder(result?.order || order)
      setNotice('付款证明已提交，等待管理员人工审核。')
    } catch (submitError) {
      setError(submitError.message || '付款证明提交失败')
    } finally {
      setBusy('')
    }
  }

  async function askRefund() {
    setBusy('refund')
    setError('')
    setNotice('')
    try {
      const result = await requestVipOrderRefund(orderNo, {
        contact: form.contact,
        reason: form.reason,
      })
      setOrder(result?.order || order)
      setNotice('退款申请已创建，后台会人工处理。')
    } catch (refundError) {
      setError(refundError.message || '退款申请提交失败')
    } finally {
      setBusy('')
    }
  }

  const qrEndpoint = vipPaymentQrEndpoint(form.payChannel)

  useEffect(() => {
    if (!order) return undefined
    const controller = new AbortController()
    let objectUrl = ''
    setQrLoading(true)
    setQrLoadError('')
    setQrObjectUrl('')
    fetchVipPaymentQr(qrEndpoint, controller.signal)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setQrObjectUrl(objectUrl)
      })
      .catch((qrError) => {
        if (qrError.name === 'AbortError') return
        if (qrError.status === 401) {
          setQrLoadError('请先登录后查看收款码')
        } else if (qrError.code === 'VIP_ORDER_REQUIRED' || qrError.status === 403) {
          setQrLoadError('请先创建订单后查看收款码')
        } else {
          setQrLoadError('收款码暂不可用，请联系客服')
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setQrLoading(false)
      })
    return () => {
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [order, qrEndpoint])

  function changePayChannel(payChannel) {
    setForm((current) => ({ ...current, payChannel }))
  }

  return (
    <div className="page-container vip-page">
      <Link className="secondary-button vip-back" to="/vip">
        <ArrowLeft size={16} />
        返回 VIP 权益页
      </Link>

      <section className="page-heading compact-heading vip-heading">
        <div>
          <span className="eyebrow">VIP ORDER</span>
          <h1>VIP 订单详情</h1>
          <p>请按订单金额付款，并在付款备注中填写备注码，便于人工审核。</p>
        </div>
        <CreditCard size={22} />
      </section>

      {error && <div className="admin-notice error" role="alert">{error}</div>}
      {notice && <div className="admin-notice success" role="status">{notice}</div>}

      {loading ? (
        <section className="profile-card"><p className="profile-muted">正在加载订单...</p></section>
      ) : order ? (
        <section className="vip-order-layout">
          <article className="profile-card vip-order-card">
            <header className="profile-card-heading">
              <span className="profile-card-icon"><Clock3 size={20} /></span>
              <div>
                <h2>{order.planName}</h2>
                <p>{STATUS_LABELS[order.status] || order.status}</p>
              </div>
            </header>
            <dl className="vip-order-meta">
              <div><dt>订单号</dt><dd>{order.orderNo}</dd></div>
              <div><dt>套餐时长</dt><dd>{order.durationDays || '-'} 天</dd></div>
              <div><dt>应付金额</dt><dd>¥{order.amountYuan}</dd></div>
              <div><dt>付款备注码</dt><dd>{order.payRemark}</dd></div>
              <div><dt>创建时间</dt><dd>{order.createdAt || '-'}</dd></div>
            </dl>
            <p className="profile-muted">{paymentConfig.notice}</p>
          </article>

          <article className="profile-card vip-pay-card">
            <header className="profile-card-heading">
              <span className="profile-card-icon"><CreditCard size={20} /></span>
              <div>
                <h2>个人收款码</h2>
                <p>付款截图只作为证明，后台确认后才会开通 VIP。</p>
              </div>
            </header>
            <div className="vip-channel-switch">
              <button type="button" className={form.payChannel === 'wechat' ? 'is-active' : ''} onClick={() => changePayChannel('wechat')}>微信</button>
              <button type="button" className={form.payChannel === 'alipay' ? 'is-active' : ''} onClick={() => changePayChannel('alipay')}>支付宝</button>
            </div>
            {qrLoading ? (
              <p className="profile-muted">正在加载收款码...</p>
            ) : qrLoadError ? (
              <p className="admin-notice error" role="alert">{qrLoadError}</p>
            ) : !qrObjectUrl ? (
              <p className="profile-muted">收款码准备中...</p>
            ) : (
              <img
                className="vip-pay-qr"
                key={qrObjectUrl}
                src={qrObjectUrl}
                alt={`${form.payChannel === 'wechat' ? '微信' : '支付宝'}收款码`}
                onError={() => setQrLoadError('收款码加载失败，请联系客服')}
              />
            )}
          </article>

          <form className="profile-card vip-proof-card" onSubmit={submitProof}>
            <header className="profile-card-heading">
              <span className="profile-card-icon"><Upload size={20} /></span>
              <div>
                <h2>提交付款证明</h2>
                <p>暂未接入上传系统时，可先填写付款截图 URL。</p>
              </div>
            </header>
            <label><span>付款昵称</span><input value={form.payNickname} onChange={(event) => setForm((current) => ({ ...current, payNickname: event.target.value }))} required /></label>
            <label><span>付款时间</span><input type="datetime-local" value={form.payTime} onChange={(event) => setForm((current) => ({ ...current, payTime: event.target.value }))} required /></label>
            <label><span>付款截图 URL</span><input type="url" value={form.proofImageUrl} onChange={(event) => setForm((current) => ({ ...current, proofImageUrl: event.target.value }))} required /></label>
            <button className="primary-button profile-card-button" type="submit" disabled={busy === 'proof'}>
              <Upload size={16} />
              {busy === 'proof' ? '提交中' : '提交付款证明'}
            </button>
          </form>

          <article className="profile-card vip-refund-card">
            <header className="profile-card-heading">
              <span className="profile-card-icon"><LifeBuoy size={20} /></span>
              <div>
                <h2>客服与退款</h2>
                <p>退款只做人工处理记录，不会自动退款。</p>
              </div>
            </header>
            <label><span>联系方式</span><input value={form.contact} onChange={(event) => setForm((current) => ({ ...current, contact: event.target.value }))} placeholder="邮箱 / 微信 / Telegram" /></label>
            <label><span>说明</span><textarea rows={3} value={form.reason} onChange={(event) => setForm((current) => ({ ...current, reason: event.target.value }))} placeholder="说明开通失败、重复付款或退款原因" /></label>
            <button className="secondary-button profile-card-button" type="button" disabled={busy === 'refund'} onClick={askRefund}>
              <LifeBuoy size={16} />
              {busy === 'refund' ? '提交中' : '申请退款 / 联系客服'}
            </button>
          </article>
        </section>
      ) : (
        <section className="profile-card"><p className="profile-muted">未找到订单。</p></section>
      )}
    </div>
  )
}

export default VipOrderPage
